import {
	useState,
	useEffect,
} from "react";
import {
	useParams,
	useNavigate
} from "react-router-dom";
import {
	Box,
	Grid,
	Paper,
	Button,
	Chip,
} from '@mui/material';
import ClientService from '../../services/ClientService'
import ClientProfileComponent from '../../components/client/ClientProfile'
import ClientNote from '../../components/client/profile/ClientNote'
import ClientProfileClass from '../../components/client/profile/ClientProfileClass'
import ClientLocation from '../../components/client/profile/ClientLocation'
import ClientUser from '../../components/client/profile/ClientUser'
import ClientTags from "../../components/client/profile/ClientTags";
import AuthProvider from "../../providers/AuthProvider";
import CurrentClientProvider from "../../providers/CurrentClientProvider";
import SectionTitle from "../../components/common/SectionTitle";
import TipContext from "../../providers/GlobalTip";
import { trackEvent, handleTrackError } from "../../services/AmplitudeService";

function extractApiErrorMessage(error, fallbackMessage) {
	const responseData = error?.response?.data

	if (typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
		return responseData.message.trim()
	}

	if (typeof responseData?.error === 'string' && responseData.error.trim() !== '') {
		return responseData.error.trim()
	}

	if (typeof responseData === 'string' && responseData.trim() !== '') {
		return responseData.trim()
	}

	if (typeof error?.message === 'string' && error.message.trim() !== '') {
		return error.message.trim()
	}

	return fallbackMessage
}

export function isClientAiForecastEnabled(client) {
	if (typeof client?.featureFlagsMap === 'object' && client?.featureFlagsMap !== null) {
		return !!client.featureFlagsMap['weekly-plan-ui']
	}

	if (typeof client?.featureFlags === 'object' && client?.featureFlags !== null && !Array.isArray(client.featureFlags)) {
		return !!client.featureFlags['weekly-plan-ui']
	}

	return false
}

export function isClientAiForecastRunActive(batchStatus) {
	return batchStatus === 'queued' || batchStatus === 'pending'
}

export function buildClientAiForecastConflictState(conflict, existingAiForecast = {}) {
	const activeStatus = typeof conflict?.active_status === 'string' ? conflict.active_status.trim().toUpperCase() : ''
	const batchStatus = activeStatus === 'RUNNING' ? 'pending' : 'queued'
	const batchLabel = batchStatus === 'pending' ? 'Pending' : 'Queued'
		const batchReason = conflict?.message || conflict?.error || existingAiForecast?.batch_reason || 'AI forecast is already running for this client. Wait for the current request to finish before submitting another run.'

	return {
		...(existingAiForecast || {}),
		batch_status: batchStatus,
		batch_label: batchLabel,
		batch_reason: batchReason,
		batch_can_resubmit: batchStatus === 'pending',
		batch_trigger_request_id: conflict?.active_trigger_request_id || existingAiForecast?.batch_trigger_request_id || null,
		batch_updated_at: conflict?.active_updated_at || existingAiForecast?.batch_updated_at || null,
	}
}

export function getClientAiForecastState(client) {
	const aiForecast = client?.ai_forecast || {}
	const eligibleCount = Number(aiForecast.eligible_count || 0)
	const hasAiForecastMetadata = Object.keys(aiForecast).length > 0
	const rawBatchStatus = typeof aiForecast.batch_status === 'string' ? aiForecast.batch_status : 'idle'
	const batchStatus = rawBatchStatus || 'idle'
	const batchReason = aiForecast.batch_reason || null
	const isActiveRun = isClientAiForecastRunActive(batchStatus)
	const batchCanResubmit = !!aiForecast.batch_can_resubmit || batchStatus === 'pending' || batchStatus === 'failed'
	const batchStatusLabelMap = {
		pending: 'Pending',
		failed: 'Failed',
		completed: 'Completed',
		queued: 'Queued',
	}
	const batchStatusColorMap = {
		pending: 'warning',
		failed: 'error',
		completed: 'success',
		queued: 'info',
	}
	const batchLabel = aiForecast.batch_label || batchStatusLabelMap[batchStatus] || null
	const batchColor = batchStatusColorMap[batchStatus] || 'default'
	const buttonLabel = batchCanResubmit ? 'AI Forecast Client Re-Submit' : 'AI Forecast Client'
	const buttonColor = batchStatus === 'pending' ? 'warning' : batchStatus === 'failed' ? 'error' : 'primary'

	if (hasAiForecastMetadata) {
		const eligible = !!aiForecast.eligible || eligibleCount > 0

		return {
			eligible,
			eligibleCount,
			reason: aiForecast.reason || (eligible ? null : 'AI forecast is not enabled for this client.'),
			batchStatus,
			batchLabel,
			batchReason,
			batchCanResubmit,
			isActiveRun,
			buttonLabel,
			buttonColor,
			statusChipColor: batchColor,
		}
	}

	const eligible = isClientAiForecastEnabled(client)

	return {
		eligible,
		eligibleCount: 0,
		reason: eligible ? null : 'AI forecast is not enabled for this client.',
		batchStatus,
		batchLabel,
		batchReason,
		batchCanResubmit,
		isActiveRun,
		buttonLabel,
		buttonColor,
		statusChipColor: batchColor,
	}
}

const jss = {
	detailSection: {
		py: 2
	},
}

export function getIsLegacyClientAdmin(auth) {
	const roleTags = Array.isArray(auth?.user?.roleTags)
		? auth.user.roleTags.map((tag) => String(tag))
		: []
	const hasPlannerRole = roleTags.includes('2')
	const hasManagementOneRole = roleTags.includes('d76bf050-3555-4a15-8e0a-3f6c57eb31a7')
	const hasAdminPlannerPermission = auth?.checkPermission?.(['role-create', 'add-client', 'full-planning', 'dev-tools'])

	return Boolean(hasPlannerRole || hasManagementOneRole || hasAdminPlannerPermission)
}


// force rebuild
function ClientProfile(props) {
	const isV3Variant = props?.visualVariant === 'v3'
	let { client_code } = useParams()
	const [client, setClient] = useState()
	const navigate = useNavigate()
	const auth = AuthProvider.useAuth()
	const currentClientContext = CurrentClientProvider.useClient()
	const [isAdmin, setIsAdmin] = useState(false)
	const tip = TipContext.useMessage()
	const [isTriggeringAiForecast, setIsTriggeringAiForecast] = useState(false)
	const clientAiForecastState = getClientAiForecastState(client)

	useEffect(() => {
		document.title = 'Retail ORBIT: Client Profile'
	}, [])

	// is admin
	useEffect(() => {
		if (getIsLegacyClientAdmin(auth)) {
			setIsAdmin(true)
			return
		}

		setIsAdmin(false)
	}, [auth])

	useEffect(() => {
		loadClient()
	}, [client_code])

	/**
	 * Generates all plans for a client
	 *
	 * @param {Object} e
	 *
	 * @return {void}
	 */
	const generateNewClassPlans = async () => {
		if (!client?.Id) {
			tip.clear()
			tip.show('Client profile is still loading. Please try again in a moment.', 5)
			return
		}

		// confirm user knows what this Does
		if (!window.confirm('Are you sure you want to generate new class plans? This will generate empty plans for all plannable locations classes for this client.')) {
			return
		}

		tip.showSuccess('Generating new class plans... this will take several minutes. You may continue browsing.')

		try {
			await ClientService.generateNewClassPlans(client.Id)

			trackEvent('Client Profile Event', {
				action: 'Generated New Class Plans',
				clientCode: client_code
			})
		} catch(e) {
			console.log(e)

			// clear current tipContext messages
			tip.clear()

			tip.show(extractApiErrorMessage(e, 'Unable to generate class plans for this client.'), 5)

			handleTrackError({ error: e, pageName: 'Client Profile', additionalMetadata: { clientCode: client_code, action: 'Generate New Class Plans' } })
		}
	}

	/**
	 * Generate new month plans for the client
	 *
	 * @return {void}
	 */
	const generateNewMonthPlans = async () => {
		// confirm user knows what this Does
		if (!window.confirm('Are you sure you want to generate new plans for this month? Be sure that end of month data has been finalized for the previous month.')) {
			return
		}

		tip.showSuccess('Generating new month plans... this will take several minutes. You may continue browsing.')

		try {
			await ClientService.generateNewMonthPlans(client.Id)

			trackEvent('Client Profile Events', {
				action: 'Generated New Month Plans',
				clientCode: client_code
			})
		} catch(e) {
			console.log(e)

			// clear current tipContext messages
			tip.clear()

			tip.show(e.response?.data?.message, 5)

			handleTrackError({ error: e, pageName: 'Client Profile', additionalMetadata: { clientCode: client_code, action: 'Generate New Month Plans' } })
		}
	}

	/**
	 * Sends email to client notifying that their plans for the new month were published
	 *
	 * @return {void}
	 */
	const notifyPlansPublished = async () => {
		// confirm user knows what this Does
		if (!window.confirm('Are you sure you want to send a notification to the client that their plans have been published?')) {
			return
		}

		tip.showSuccess('Sending notification to client...')

		try {
			await ClientService.notifyPlansPublished(client.Id)
			tip.showSuccess('Notification sent to client.')

			trackEvent('Client Profile Event', {
				action: 'Notified Plans Published',
				clientCode: client_code
			})
		} catch(e) {
			console.log(e)

			// clear current tipContext messages
			tip.clear()

			tip.show(e.response?.data?.message, 5)

			handleTrackError({ error: e, pageName: 'Client Profile', additionalMetadata: { clientCode: client_code, action: 'Notify Plans Published' } })
		}
	}

	/**
	 * Queue hierarchy AI forecast generation for the full client.
	 *
	 * @return {void}
	 */
	const triggerAiForecast = async () => {
		if (!clientAiForecastState.eligible) {
			tip.clear()
			tip.show(clientAiForecastState.reason || 'AI forecast is not enabled for this client.', 5)
			return
		}

		if (clientAiForecastState.isActiveRun) {
			tip.clear()
			tip.show(
				clientAiForecastState.batchReason || 'AI forecast is already running for this client. Wait for the current request to finish before submitting another run.',
				5,
			)
			return
		}

		if (!window.confirm('Are you sure you want to queue AI forecast generation for this client? Updated planning data will appear after processing completes.')) {
			return
		}

		setIsTriggeringAiForecast(true)
		tip.showSuccess('Queueing AI forecast for client...')

		try {
			const response = await ClientService.triggerAiForecast(client.Id)
			setClient((prev) => prev ? ({
				...prev,
				ai_forecast: {
					...(prev.ai_forecast || {}),
					batch_status: 'pending',
					batch_label: 'Pending',
					batch_reason: 'AI forecast results are still pending downstream processing.',
					batch_can_resubmit: true,
					batch_trigger_request_id: response?.trigger_request_id || prev?.ai_forecast?.batch_trigger_request_id || null,
				},
			}) : prev)
			tip.showSuccess(response?.message || 'AI forecast has been queued for this client. Updated planning data will appear after processing completes.')

			trackEvent('Client Profile Event', {
				action: 'Triggered AI Forecast Client',
				clientCode: client_code,
			})
		} catch(e) {
			console.log(e)
			const errorMessage = extractApiErrorMessage(e, 'Unable to queue AI forecast for this client.')

			tip.clear()
			if (e?.response?.status === 409) {
				setClient((prev) => prev ? ({
					...prev,
					ai_forecast: buildClientAiForecastConflictState(e?.response?.data, prev.ai_forecast),
				}) : prev)
			}
			tip.show(errorMessage, 5)

			handleTrackError({ error: e, pageName: 'Client Profile', additionalMetadata: { clientCode: client_code, action: 'Trigger AI Forecast Client' } })
		} finally {
			setIsTriggeringAiForecast(false)
		}
	}

	async function loadClient() {
		try {
			const _client = await ClientService.getClientByCode(client_code)
			setClient(_client)
		} catch(e) {
			navigate('/')
		}
	}

	function handleClientSave(nextClient) {
		setClient(nextClient)
		currentClientContext?.setClient?.(nextClient)
	}

	return <Grid container spacing={2} sx={jss.detailSection} className={isV3Variant ? 'v3-profile-main-grid' : undefined}>
		<Grid item xs={12} sm={6}>
			{isAdmin ? 
				<Paper elevation={2} sx={{mb:2}} className={isV3Variant ? 'v3-profile-admin-tools' : undefined}>
					<Box sx={{mb:2}} spacing={2}>
						<SectionTitle label="Client Admin Tools" type="primary"/>
					</Box>
					<Button type="button" variant="contained" onClick={generateNewClassPlans} disabled={!client} sx={{ml: 2, mb: 2}}>Generate Plans for New Classes</Button>
					{client?.IsManualEntry ? <Button type="button" variant="contained" onClick={generateNewMonthPlans} sx={{ml: 2, mb: 2}}>Generate Plans for New Month</Button> : null}
					<Button type="button" variant="contained" onClick={notifyPlansPublished} sx={{ml: 2, mb: 2}}>Send Plans Published Notification</Button>
					<span title={[clientAiForecastState.reason, clientAiForecastState.batchReason].filter(Boolean).join(' • ')}>
						<Button
							type="button"
							data-testid="client-profile-ai-forecast-trigger"
							variant="contained"
							color={clientAiForecastState.buttonColor}
							onClick={triggerAiForecast}
							disabled={!client || !clientAiForecastState.eligible || clientAiForecastState.isActiveRun || isTriggeringAiForecast}
							sx={{ml: 2, mb: 2}}
						>
							{isTriggeringAiForecast ? 'Queueing AI Forecast...' : clientAiForecastState.buttonLabel}
						</Button>
					</span>
					{clientAiForecastState.batchLabel ? (
						<Chip
							size="small"
							label={clientAiForecastState.batchLabel}
							color={clientAiForecastState.statusChipColor}
							sx={{ml: 1, mb: 2}}
						/>
					) : null}
				</Paper>
			: null}
			<ClientProfileComponent client={client} onSave={handleClientSave} isAdmin={isAdmin} visualVariant={props?.visualVariant} />
		</Grid>
		<Grid item xs={12} sm={6}>
			{isAdmin ?
				<Box sx={{mb:2}}>
					<ClientNote client={client} />
				</Box>
			: null}
			<Box sx={{mb:2}}>
				<ClientTags client={client} isAdmin={isAdmin} />
			</Box>
			<Box sx={{mb:2}}>
				<ClientProfileClass client={client} isAdmin={isAdmin} />
			</Box>
			<Box sx={{mb:2}}>
				<ClientLocation
					client={client}
					isAdmin={isAdmin}
					onClientAiForecastConflict={(conflict) => {
						setClient((prev) => prev ? ({
							...prev,
							ai_forecast: buildClientAiForecastConflictState(conflict, prev.ai_forecast),
						}) : prev)
					}}
				/>
			</Box>
			<Box sx={{mb:2}}>
				<ClientUser client={client} isAdmin={isAdmin} />
			</Box>
		</Grid>
	</Grid>
}
export default ClientProfile